// frontend/src/services/userManualService.js - Complete Employee User Manual
// This service provides comprehensive documentation for all ExoTrack employee portal features

class UserManualService {
  /**
   * Get all user manual data including categories, FAQs, tips, and troubleshooting
   */
  async getAllData() {
    return {
      categories: this.getCategories(),
      faqs: this.getFAQsData(),
      quickTips: this.getQuickTipsData(),
      troubleshooting: this.getTroubleshootingData(),
    };
  }

  /**
   * Get all manual categories with articles
   */
  getCategories() {
    return [
      {
        id: 'getting-started',
        icon: 'Rocket',
        title: 'Getting Started',
        description: 'Learn the basics of using ExoTrack',
        articles: [
          {
            id: 'first-login',
            title: 'Your First Login',
            tags: ['basics', 'setup'],
            content: `# Welcome to ExoTrack!

## What is ExoTrack?
ExoTrack is a comprehensive attendance and workforce management system designed to make tracking your work hours, leave requests, and overtime simple and efficient.

## First-Time Login
When you first log in to ExoTrack, you'll need:
- **Your email address** (provided by your administrator)
- **A temporary password** (sent to your email)

**Steps:**
1. Go to the ExoTrack login page
2. Enter your email address
3. Enter the temporary password from your email
4. You'll be prompted to change your password on first login
5. Create a strong password (minimum 8 characters with letters and numbers)

## Your Dashboard
After logging in, you'll see your employee dashboard which displays:
- **Current attendance status** (clocked in/out)
- **Today's work hours**
- **Leave balance** (annual, sick, personal leave)
- **Recent attendance history**
- **Notifications** and important updates

## Navigation Menu
The left sidebar contains quick links to:
- **Dashboard** - Your overview page
- **Attendance** - Clock in/out and view status
- **History** - View your attendance records
- **Leave Requests** - Apply for and manage leave
- **Overtime Requests** - Request overtime approval
- **Profile** - Update your personal information

## Need Help?
Click the **User Manual** link in the sidebar anytime you need assistance.`,
          },
          {
            id: 'dashboard-overview',
            title: 'Understanding Your Dashboard',
            tags: ['basics', 'navigation'],
            content: `# Dashboard Overview

## Dashboard Cards

### 1. Attendance Card (Top Left)
Shows your current clock status:
- **Clocked In** - Green indicator with clock-in time
- **Clocked Out** - Gray indicator
- **Live Duration Counter** - Shows real-time work hours when clocked in
- **Quick Action Button** - "Go to Clock In" or "Go to Clock Out"
- **Today's Hours** - Total hours worked today
- **This Week's Hours** - Total hours for current week

### 2. Today's Summary Card
Displays:
- **Hours Worked** - Total time for today
- **Breaks** - Number of breaks taken
- **Location** - Your work location
- **View History** button for detailed records

### 3. Leave Balance Card
Shows your remaining leave days:
- **Annual Leave** - Vacation days
- **Sick Leave** - Medical leave
- **Personal Leave** - Personal time off
- **Request Leave** button for quick access

### 4. Monthly Statistics Card
Tracks this month's performance:
- **Total Hours** - Hours worked this month
- **Days Worked** - Number of working days
- **Average Hours/Day** - Your daily average
- **Progress Bar** - Visual indicator toward monthly target

## Recent Activity Section

### Recent Attendance (Left Panel)
- Last 5 attendance records
- Shows date, clock-in/out times, and duration
- Status indicators (Approved, Pending, In Progress)
- Click "View All" to see complete history

### Notifications (Right Panel)
- System messages and updates
- Leave request status changes
- Overtime approvals
- Important announcements
- Click notifications to mark as read
- "Mark All Read" button available

## Online/Offline Indicator
Top right corner shows your connection status:
- **Green dot** - Online and synced
- **Red dot** - Offline (data will sync when reconnected)

## Refresh Data
Dashboard auto-refreshes every 5 minutes. Manual refresh available via browser refresh button.`,
          },
          {
            id: 'system-requirements',
            title: 'System Requirements & Browser Support',
            tags: ['technical', 'setup'],
            content: `# System Requirements

## Supported Browsers
ExoTrack works best on modern browsers:

### Desktop
- **Chrome** 90+ (Recommended)
- **Firefox** 88+
- **Safari** 14+
- **Edge** 90+

### Mobile
- **Chrome Mobile** (Android)
- **Safari Mobile** (iOS)
- **Samsung Internet**

## Device Requirements

### For Camera/Photo Features
- Device with working camera
- Camera permissions enabled
- Good lighting for clear photos

### For Location Features
- GPS/Location services enabled
- Location permissions granted to browser
- Stable internet connection

## Internet Connection
- **Minimum:** 1 Mbps for basic features
- **Recommended:** 5+ Mbps for smooth operation
- **Mobile data:** 3G or better

## Permissions Needed
When first using certain features, you'll be asked to grant:
1. **Camera access** - For attendance photos
2. **Location access** - For field work tracking
3. **Notifications** - For important alerts

## Security Notes
- Always use HTTPS (secure connection)
- Never share your login credentials
- Log out when using shared computers
- Use strong, unique passwords

## Troubleshooting Browser Issues
If ExoTrack isn't working properly:
1. Clear browser cache and cookies
2. Update to the latest browser version
3. Disable browser extensions temporarily
4. Try incognito/private mode
5. Contact IT support if issues persist`,
          },
        ],
      },
      {
        id: 'attendance',
        icon: 'Clock',
        title: 'Attendance Management',
        description: 'Clock in/out, track work hours, and manage breaks',
        articles: [
          {
            id: 'clock-in-out',
            title: 'How to Clock In and Clock Out',
            tags: ['attendance', 'basics', 'daily'],
            content: `# Clocking In and Out

## Clocking In

### From Dashboard
1. Click **"Go to Clock In"** button on the Attendance card
2. You'll be redirected to the Attendance page

### From Attendance Page
1. Navigate to **Attendance** from the sidebar
2. Click the **"Clock In"** button (large blue button)
3. If required by your company:
   - Allow camera access
   - Take your photo (smile!)
   - Allow location access
4. Click **"Submit Clock In"**
5. ✅ Success! You're now clocked in

### What Happens After Clock In
- Status changes to **"Clocked In"**
- Live duration counter starts
- Clock-in time is recorded
- Photo and location saved (if required)

## Clocking Out

### When to Clock Out
- At the end of your workday
- Before leaving the office
- After completing your shift

### Steps
1. Go to **Attendance** page
2. Click the **"Clock Out"** button (large red button)
3. If required:
   - Take your photo
   - Confirm location
4. Add optional notes about your day
5. Click **"Submit Clock Out"**
6. ✅ Your work session is complete!

### What Happens After Clock Out
- Total hours calculated automatically
- Duration saved to your record
- Attendance marked complete
- Data synced to system

## Important Notes

### Camera Requirements
- Ensure good lighting
- Face should be clearly visible
- No hats or sunglasses
- Photo is for verification only

### Location Tracking
- Must be within approved work locations
- GPS accuracy: within 100 meters
- Field workers: special locations may apply
- Contact admin if location issues occur

### If You Forget to Clock Out
- Contact your supervisor/admin
- They can manually adjust your record
- Provide actual clock-out time
- May require approval

### Multiple Clock-Ins
- If you clock in twice by mistake, the latest time is used
- Each clock-in should match with a clock-out
- Unclosed sessions show as "In Progress"

## Best Practices
✅ Clock in right when you start work
✅ Clock out before leaving
✅ Take clear, well-lit photos
✅ Ensure location permissions are enabled
✅ Add notes if working from different location
❌ Don't let others clock in/out for you
❌ Don't share your login credentials
❌ Don't try to manipulate location data`,
          },
          {
            id: 'break-management',
            title: 'Taking Breaks',
            tags: ['attendance', 'breaks'],
            content: `# Managing Your Breaks

## Starting a Break

### When to Use Breaks
- Lunch breaks
- Coffee/tea breaks
- Prayer breaks
- Smoking breaks
- Short personal breaks

### How to Start a Break
1. Go to **Attendance** page
2. Look for the **"Break"** section
3. Click **"Start Break"**
4. Select break type:
   - Lunch
   - Coffee
   - Personal
   - Other
5. Add optional reason/notes
6. Click **"Confirm"**

### During Your Break
- Break timer starts automatically
- Your work time counter pauses
- Status shows "On Break"
- System tracks break duration

## Ending a Break

### Steps
1. Return to **Attendance** page
2. Click **"End Break"**
3. Break duration is recorded
4. Work time counter resumes
5. Status returns to "Clocked In"

## Break Rules

### Company Policies
- Maximum break duration (usually 60 minutes for lunch)
- Number of breaks allowed per day
- Paid vs unpaid breaks
- Break time tracking

*Check with your HR department for specific break policies*

### Automatic Break Tracking
- System records all break times
- Total break time visible on history
- Breaks deducted from work hours
- Monthly break summary available

## Multiple Breaks in a Day
You can take multiple breaks:
1. Morning coffee break
2. Lunch break
3. Afternoon break

Each break is tracked separately and totaled at end of day.

## Forgot to End Break?
If you forget to end a break:
- Contact your supervisor
- They can manually close the break
- Provide actual break end time
- May require approval

## Best Practices
✅ Always start/end breaks properly
✅ Keep breaks within allowed duration
✅ Use correct break type
✅ Add notes for unusual breaks
❌ Don't forget to end your break
❌ Don't exceed break time limits
❌ Don't abuse the break system`,
          },
          {
            id: 'attendance-history',
            title: 'Viewing Your Attendance History',
            tags: ['attendance', 'reports', 'history'],
            content: `# Attendance History

## Accessing Your History

### From Sidebar
1. Click **"History"** in the left sidebar
2. Your complete attendance record appears

### From Dashboard
1. Scroll to **"Recent Attendance"** section
2. Click **"View All"** button

## Understanding History View

### Record Columns
- **Date** - The working date
- **Clock In** - Time you started work
- **Clock Out** - Time you finished work
- **Duration** - Total hours worked
- **Breaks** - Number and duration of breaks
- **Location** - Where you worked from
- **Status** - Approved/Pending/Rejected

### Status Indicators
- 🟢 **Approved** - Attendance confirmed
- 🟡 **Pending** - Awaiting review
- 🔴 **Rejected** - Needs correction
- ⚪ **In Progress** - Currently working

## Filtering Your History

### By Date Range
1. Click **"Filter"** button
2. Select **"Start Date"**
3. Select **"End Date"**
4. Click **"Apply"**

### Quick Filters
- **Today** - Today's records only
- **This Week** - Current week
- **This Month** - Current month
- **Last Month** - Previous month
- **Custom Range** - Choose any dates

### By Status
Filter by:
- All records
- Approved only
- Pending only
- Rejected only

## Viewing Record Details

### Click on Any Record
- See full attendance information
- View clock-in/out photos (if available)
- Check location coordinates
- See break details
- View admin notes/comments

## Exporting Your History

### Download Options
1. Click **"Export"** button
2. Choose format:
   - **PDF** - Formatted report
   - **Excel** - Spreadsheet data
   - **CSV** - Raw data
3. Select date range
4. Click **"Download"**

### What's Included in Export
- All attendance records for selected period
- Clock-in/out times
- Total hours worked
- Break information
- Overtime hours (if applicable)
- Leave days marked

## Monthly Summary

### Total Hours Worked
- Sum of all working hours
- Broken down by week
- Compared to expected hours
- Overtime highlighted

### Attendance Rate
- Days attended vs total working days
- Percentage attendance
- Absent days
- Late arrivals

### Average Working Hours
- Average hours per day
- Most productive days
- Shortest/longest days

## Common Questions

**Q: Why is my record pending?**
A: New records may need admin approval, especially if:
- Worked from unusual location
- Unusual working hours
- Missing clock-out
- Manual adjustments needed

**Q: Can I edit my history?**
A: No, employees cannot edit records. Contact your supervisor if corrections needed.

**Q: How far back can I see?**
A: Usually 1 year of history is available. Older records may require admin access.

**Q: Are records permanent?**
A: Yes, attendance data is securely stored for compliance and payroll purposes.`,
          },
          {
            id: 'location-tracking',
            title: 'Location & Field Work Tracking',
            tags: ['attendance', 'location', 'field-work'],
            content: `# Location Tracking

## Why Location Tracking?

ExoTrack uses location data to:
- Verify you're at approved work locations
- Support field workers
- Prevent attendance fraud
- Track remote work locations
- Ensure employee safety

## Enabling Location Access

### First Time Setup
1. When prompted, click **"Allow"** for location access
2. Browser asks for permission
3. Click **"Allow"** in browser dialog
4. Location icon appears (green = enabled)

### If You Denied Permission
**On Desktop:**
1. Click the lock/info icon in address bar
2. Find "Location" setting
3. Change to "Allow"
4. Refresh the page

**On Mobile:**
1. Go to browser settings
2. Find ExoTrack in sites list
3. Enable location permission
4. Restart browser

## Office-Based Work

### Approved Locations
- Your company has set approved work locations
- Clock-in checks if you're within range
- Typical radius: 100-500 meters
- If outside range, you'll see a warning

### What Happens When Clocking In
1. System captures your GPS coordinates
2. Compares with approved locations
3. ✅ If within range: Clock-in proceeds
4. ⚠️ If outside range: May need approval or explanation

## Field Work

### What is Field Work?
Working outside the standard office location:
- Client visits
- Site inspections
- Remote work
- Traveling for business
- Home office (if approved)

### Clocking In from Field
1. Go to Attendance page
2. System detects you're not at office
3. Select **"Field Work"** option
4. Choose field work type:
   - Client Visit
   - Site Work
   - Remote Work
   - Other
5. Add location description (e.g., "Client ABC Office")
6. Take photo
7. Submit clock-in

### Field Work Approval
- May require manager approval
- Some employees have auto-approval
- Check with HR about your privileges
- Unapproved field work shows as "Pending"

## Location Privacy

### What Data is Collected?
- GPS coordinates (latitude/longitude)
- Time of collection
- Accuracy of GPS reading
- Device information

### What is NOT Collected
- Continuous tracking
- Location when not clocked in
- Personal location history
- Any data outside work hours

### Your Privacy Rights
- Location only captured during clock-in/out
- Data used only for attendance verification
- Access restricted to authorized personnel
- Can request deletion per company policy

## Troubleshooting Location Issues

### "Location Not Detected"
**Try these steps:**
1. Ensure GPS is enabled on device
2. Move near a window (better GPS signal)
3. Restart browser
4. Check location permissions
5. Try again in 1-2 minutes

### "Outside Approved Location"
**If you're at the office but getting this error:**
1. Move to different part of building
2. Wait for better GPS accuracy
3. Contact your supervisor
4. They can manually approve

**If you're actually outside office:**
1. Select "Field Work" option
2. Provide reason/description
3. Request approval if needed

### Poor GPS Accuracy
**Causes:**
- Being indoors
- Tall buildings nearby
- Bad weather
- Device limitations

**Solutions:**
- Move near window
- Go outside briefly
- Wait for better signal
- Use "Manual Location" if available

## Best Practices
✅ Enable location permissions
✅ Ensure GPS is active
✅ Clock in from your actual location
✅ Add clear descriptions for field work
✅ Keep device location settings updated
❌ Don't try to fake your location
❌ Don't share your credentials with others
❌ Don't clock in from home if not approved
❌ Don't disable location services to bypass checks`,
          },
        ],
      },
      {
        id: 'leave-management',
        icon: 'Calendar',
        title: 'Leave & Time Off',
        description: 'Request leave, check balances, and manage approvals',
        articles: [
          {
            id: 'leave-types',
            title: 'Understanding Leave Types',
            tags: ['leave', 'policies'],
            content: `# Types of Leave

## Annual Leave (Vacation)

### What is it?
- Paid time off for vacations and personal rest
- Accrues throughout the year
- Must be planned in advance

### Eligibility
- Usually after probation period
- Accrual based on tenure
- Check your contract for specifics

### Usage
- Book in advance (typically 2 weeks notice)
- Subject to manager approval
- Cannot exceed annual balance
- May carry over to next year (company policy dependent)

### Balance
- View in Dashboard leave card
- Increases monthly/yearly
- Deducted when leave is approved
- Track usage in Leave Requests page

## Sick Leave

### What is it?
- Time off for illness or medical appointments
- Usually requires medical documentation
- May need doctor's note for extended periods

### When to Use
- Personal illness
- Medical appointments
- Recovery from surgery
- Contagious illness (protect others)

### Documentation Required
- 1 day: Usually no documentation
- 2-3 days: May need medical certificate
- 4+ days: Doctor's note required
- Check company policy for specifics

### Emergency Sick Leave
- Call your supervisor immediately
- Submit leave request as soon as possible
- Provide medical documentation when you return
- May be approved retroactively

## Emergency Leave

### What is it?
- Urgent, unplanned leave for emergencies
- Family emergencies
- Unexpected critical situations
- Usually limited days per year

### Examples
- Death in family
- Serious family illness
- Natural disasters
- Critical personal matters

### How to Request
1. Call/message supervisor immediately
2. Submit formal request ASAP
3. Provide supporting documentation
4. May need HR approval

## Personal Leave

### What is it?
- Leave for personal reasons
- May be paid or unpaid (company policy)
- Usually limited allocation

### Common Uses
- Family events
- Personal errands
- Religious observances
- Personal development

## Unpaid Leave

### What is it?
- Time off without pay
- When paid leave exhausted
- Extended absences
- Sabbaticals

### Considerations
- Affects salary
- May affect benefits
- Requires advance approval
- Usually needs higher-level approval

## Parental Leave

### Types
- **Maternity Leave** - For mothers
- **Paternity Leave** - For fathers
- **Adoption Leave** - For adoptive parents

### Duration
- Varies by country and company
- Check local labor laws
- Review company policy
- May include paid and unpaid portions

### Planning
- Notify HR early (3+ months before)
- Complete required documentation
- Plan for coverage during absence
- Coordinate return-to-work date

## Special Leave Types

### Bereavement Leave
- Death of family member
- Usually 3-5 days
- May require death certificate
- Immediate family typically covered

### Study Leave
- For examinations or courses
- May require proof of enrollment
- Usually limited days per year
- Often needs advance approval

### Compassionate Leave
- Serious illness of family member
- Usually short-term
- Requires documentation
- Case-by-case approval

## Leave Balance Calculation

### How Balances Work
Starting balance + Accruals - Used leave = Current balance

### Accrual Methods
- **Annual** - Full allocation at start of year
- **Monthly** - Fraction each month
- **Per pay period** - Based on pay schedule

### Checking Your Balance
1. Dashboard leave card
2. Leave Requests page
3. Profile settings
4. Monthly payslip

## Leave Policies

### Notice Requirements
- Annual leave: 2-4 weeks advance
- Sick leave: Same day notification
- Emergency: ASAP
- Extended leave: 1-3 months advance

### Blackout Periods
- Peak business seasons
- Year-end closing
- Major projects
- Check company calendar

### Maximum Consecutive Days
- Company may limit continuous leave
- Usually 10-15 days maximum
- Extended leave needs special approval

### Leave Carry-Over
- Rules for unused leave
- Deadlines for usage
- Maximum carry-over limits
- Use-it-or-lose-it policies

## Important Notes
📌 Always check your company's specific leave policy
📌 Different countries have different labor laws
📌 Your contract may have special provisions
📌 When in doubt, ask HR for clarification`,
          },
          {
            id: 'request-leave',
            title: 'How to Request Leave',
            tags: ['leave', 'how-to', 'process'],
            content: `# Requesting Leave

## Before You Request

### Check Your Balance
1. Go to Dashboard
2. Look at Leave Balance card
3. Ensure sufficient days available
4. Note different leave types

### Plan Ahead
- Check team calendar
- Avoid peak business periods
- Consider blackout dates
- Coordinate with teammates

### Talk to Your Manager
- Inform them verbally first
- Discuss workload coverage
- Get informal approval
- Then submit formal request

## Submitting Leave Request

### Step-by-Step Process

**1. Navigate to Leave Requests**
- Click **"Leave Requests"** in sidebar
- OR click **"Request Leave"** button on Dashboard

**2. Click "New Leave Request"**
- Big button at top of page
- Opens leave request form

**3. Fill Out the Form**

**Leave Type***
- Select from dropdown:
  - Annual Leave
  - Sick Leave
  - Emergency Leave
  - Personal Leave
  - Other (specify)

**Date Range***
- **Start Date**: First day of leave
- **End Date**: Last day of leave
- System auto-calculates working days
- Excludes weekends/holidays

**Half-Day Option**
- Check if taking partial day
- Select morning or afternoon
- Counts as 0.5 day

**Reason***
- Brief explanation
- Required for most leave types
- Be clear and professional
- Example: "Family vacation in Dubai"

**Supporting Documents** (if required)
- Click "Upload Documents"
- Accepted formats: PDF, JPG, PNG, DOC
- Maximum 5 files
- Maximum 5MB per file
- Examples:
  - Medical certificates (sick leave)
  - Flight bookings (vacation)
  - Event invitations

**Emergency Contact** (optional)
- Phone number during leave
- Email address
- For urgent matters only

**4. Review Summary**
- Total days requested
- Leave balance before/after
- Manager who will approve
- Affected dates

**5. Submit Request**
- Click **"Submit Leave Request"**
- Confirmation message appears
- Email sent to manager
- Notification sent to you

## After Submission

### Request Status
Your request will have one of these statuses:

🟡 **Pending**
- Waiting for approval
- Manager reviewing
- You can still cancel

🟢 **Approved**
- Manager has approved
- Leave confirmed
- Balance deducted
- Appears on team calendar

🔴 **Rejected**
- Manager declined
- Reason provided in comments
- Balance not deducted
- Can submit new request

### Email Notifications
You'll receive emails for:
- ✅ Request submitted successfully
- 👀 Manager viewed request
- ✅ Request approved
- ❌ Request rejected
- 💬 Manager added comments

### Checking Status
1. Go to **Leave Requests** page
2. Your requests listed with status
3. Click on request to see details
4. View approval timeline
5. Read manager comments

## Canceling a Leave Request

### Before Approval
1. Go to **Leave Requests**
2. Find your pending request
3. Click **"Cancel Request"**
4. Confirm cancellation
5. Request removed

### After Approval
Cannot self-cancel approved leave:
1. Contact your manager
2. Explain the situation
3. They can cancel on admin side
4. Balance will be restored

## Emergency Leave Requests

### When You Can't Plan Ahead
If you need urgent leave:

1. **Call/Message Your Manager**
   - Don't just submit online
   - Speak to them first
   - Explain the situation

2. **Submit Request ASAP**
   - Even if you're already absent
   - Mark as "Emergency"
   - Provide full explanation

3. **Follow Up with Documentation**
   - Upload documents when possible
   - Medical certificates for sick leave
   - Proof for emergencies

## Multiple-Day vs Single-Day Requests

### Single Day
- Simple, quick approval
- Less documentation needed
- Minimal impact on team

### Multiple Days (2-5 days)
- May need more justification
- Ensure work coverage
- Earlier approval needed

### Extended Leave (6+ days)
- Requires advance notice (2-4 weeks)
- Detailed handover plan
- May need higher-level approval
- More documentation required

## Best Practices

### DO ✅
- ✅ Submit requests early
- ✅ Check balance first
- ✅ Provide clear reasons
- ✅ Upload required documents
- ✅ Plan work coverage
- ✅ Communicate with team
- ✅ Follow up if no response in 2 days

### DON'T ❌
- ❌ Submit last-minute requests (unless emergency)
- ❌ Exceed your balance
- ❌ Request leave during blackout periods
- ❌ Fail to upload required documents
- ❌ Assume approval without waiting
- ❌ Book non-refundable tickets before approval

## Common Mistakes to Avoid

**Mistake 1: Not checking balance**
- Always verify sufficient leave days
- Consider pending requests
- Account for already-planned leave

**Mistake 2: Wrong date range**
- Double-check start and end dates
- Verify working days calculation
- Include travel days if needed

**Mistake 3: Missing documentation**
- Upload all required documents
- Ensure files are clear/readable
- Medical certificates must be valid

**Mistake 4: Poor communication**
- Tell manager verbally first
- Explain coverage plan
- Update team calendar

## Approval Timeline

### Typical Processing Time
- **Single day leave**: 24-48 hours
- **2-5 days leave**: 2-3 business days
- **Extended leave**: 5-7 business days
- **Emergency leave**: Within 24 hours

### What Affects Approval Time
- Manager's availability
- Company workload
- Documentation completeness
- Time of year (busy seasons)

### If Delayed Beyond Expected Time
1. Check request status online
2. Send polite reminder to manager
3. Contact HR if no response after 5 days
4. Check if additional info needed

## Pro Tips

💡 **Plan around company calendar**
- Check official holiday schedule
- Avoid fiscal year-end
- Coordinate with team events

💡 **Build leave buffer**
- Don't use entire balance
- Keep emergency days available
- Plan for unexpected situations

💡 **Book early for popular periods**
- Summer vacations
- Religious holidays
- School holiday periods
- Year-end celebrations

💡 **Keep records**
- Screenshot approved requests
- Save approval emails
- Track your balance usage
- Note for payroll verification`,
          },
          {
            id: 'leave-balance',
            title: 'Managing Your Leave Balance',
            tags: ['leave', 'balance', 'tracking'],
            content: `# Leave Balance Management

## Understanding Your Leave Balance

### What is Leave Balance?
Your leave balance shows:
- **Total allocated days** per year
- **Days used** so far
- **Days remaining** available
- **Pending requests** (not yet deducted)

### Types of Balances
Each leave type has its own balance:
- Annual Leave balance
- Sick Leave balance
- Personal Leave balance
- Emergency Leave balance

## Viewing Your Leave Balance

### From Dashboard
1. Look at **Leave Balance Card**
2. See all leave types at a glance
3. Numbers show remaining days
4. Click "Request Leave" for quick access

### From Leave Requests Page
1. Go to **Leave Requests** in sidebar
2. Balance summary at top
3. Detailed breakdown available
4. Click each type for more info

### Detailed Balance View
To see comprehensive information:
1. Go to Leave Requests page
2. Click **"Balance Details"**
3. View:
   - Opening balance (start of year)
   - Accrued balance (added throughout year)
   - Used balance (approved leave taken)
   - Pending balance (awaiting approval)
   - Current available balance

## How Leave Accrues

### Annual Accrual Methods

**Method 1: Annual Allocation**
- Full year's leave granted on Jan 1
- Example: 20 days given at start
- Use throughout the year
- Resets next year

**Method 2: Monthly Accrual**
- Leave builds up each month
- Example: 1.67 days per month = 20 days/year
- Pro-rated for partial years
- Fair for new joiners

**Method 3: Per Pay Period**
- Based on pay schedule
- Bi-weekly: ~0.77 days per period
- Weekly: ~0.38 days per week
- Aligns with payroll

### Probation Period Rules
- New employees may have restrictions
- Some companies: no leave during probation
- Others: accrual starts, usage after probation
- Check your employment contract

### Part-Time Employees
- Pro-rated based on working hours
- Example: 50% time = 50% leave balance
- Calculated proportionally
- Same leave types apply

## Balance Calculations

### Basic Formula
**Current Balance = Opening + Accrued - Used - Pending**

### Example
- Opening balance (Jan 1): 20 days
- Accrued to date: 0 days (annual method)
- Used (approved leave): 5 days
- Pending (awaiting approval): 2 days
- **Available balance: 13 days**

### Pending vs Available
**Pending Leave:**
- Requests submitted, not yet approved
- Days "reserved" but not confirmed
- If rejected, returns to balance
- If approved, deducted from balance

**Available Leave:**
- Immediately usable
- Can request these days
- Excludes pending requests
- True remaining balance

## Tracking Your Usage

### Monthly Balance Statement
View your balance history:
1. Go to Leave Requests
2. Click "Balance History"
3. See month-by-month breakdown:
   - Starting balance
   - Leave taken
   - Leave added (accrual)
   - Ending balance

### Leave Usage Report
Generate detailed reports:
1. Navigate to Leave Requests
2. Click "Export Balance Report"
3. Select date range
4. Choose format (PDF/Excel)
5. Download includes:
   - All leave transactions
   - Balance changes
   - Approval details
   - Running totals

## Balance Adjustments

### When Balances Change

**Positive Adjustments (Added Days):**
- Annual accrual
- Carried over from previous year
- Company bonus leave
- Compensation for worked holidays
- Corrected errors

**Negative Adjustments (Deducted Days):**
- Approved leave taken
- Expired unused leave
- Leave buy-back programs
- Corrected errors

### Admin Adjustments
HR or admin may adjust your balance:
- System corrections
- Policy changes
- Special circumstances
- Approved exceptions

You'll receive notification of any adjustments.

## Carry-Over Rules

### What is Carry-Over?
Unused leave days transferred to next year

### Common Policies

**Policy 1: Use-It-Or-Lose-It**
- No carry-over allowed
- Must use all leave by Dec 31
- Unused days expire
- Encourages regular time off

**Policy 2: Limited Carry-Over**
- Maximum days can carry over
- Example: Up to 5 days max
- Rest expire
- Partial preservation

**Policy 3: Full Carry-Over**
- All unused days carry forward
- May have maximum cap
- Example: Max 40 days total balance
- Accumulates over years

**Policy 4: Paid Buy-Back**
- Company "buys" unused leave
- Paid at daily rate
- Limited to certain days
- Not all companies offer this

### Carry-Over Process
Usually automatic:
1. System calculates unused leave Dec 31
2. Carries forward per policy
3. Adds to new year allocation
4. Visible in January balance

## Negative Balance

### Can You Have Negative Balance?
Depends on company policy:

**Strict Policy:**
- Cannot go negative
- Cannot request more than available
- System prevents over-requests

**Flexible Policy:**
- Can go slightly negative
- With manager approval
- Must repay through future accrual
- Limited negative amount (e.g., -3 days max)

### Repaying Negative Balance
If your balance goes negative:
1. Future accruals apply to negative balance first
2. Returns to zero before adding new leave
3. Cannot request additional leave until positive
4. May affect employment separation

## Special Balance Situations

### Mid-Year Joiners
- Pro-rated annual allocation
- Based on joining date
- Example: Join July 1 = 50% of annual leave
- Some companies: full allocation
- Check your offer letter

### Employment Termination
- Final balance calculation on last day
- Unused leave may be:
  - Paid out in final settlement
  - Forfeited per policy
  - Used during notice period
- Negative balance deducted from final pay

### Extended Unpaid Leave
- Leave balance may "freeze"
- No accrual during unpaid period
- Resumes upon return
- Check specific policy

### Sabbaticals
- Special leave programs
- May affect balance accrual
- Often unpaid
- Balance handling varies

## Monitoring Tips

### Best Practices
✅ Check balance before requesting leave
✅ Track your usage monthly
✅ Plan major leave early in year
✅ Don't let leave expire
✅ Keep emergency days available
✅ Save approval confirmations
✅ Review balance statements
✅ Report discrepancies immediately

### Red Flags to Watch
⚠️ Unexpected balance decrease
⚠️ Missing accruals
⚠️ Unapproved deductions
⚠️ Incorrect carry-over
⚠️ Pending requests not reflected

If you notice any issues:
1. Screenshot your balance
2. Note the discrepancy
3. Contact HR immediately
4. Provide details of expected vs actual

## FAQs

**Q: Why is my balance different from my colleague's?**
A: Balances vary based on:
- Joining date (pro-ration)
- Employment level/role
- Leave already used
- Individual adjustments
- Part-time vs full-time

**Q: When does my balance reset?**
A: Typically January 1st, but may be:
- Your joining anniversary
- Company fiscal year start
- Check your policy

**Q: Can I donate leave to a colleague?**
A: Some companies allow leave donation:
- Usually for serious medical situations
- Requires approvals
- Check if your company has this policy

**Q: What if I disagree with my balance?**
A:
1. Check balance history
2. Review leave requests
3. Verify accrual method
4. Contact HR with specific details
5. Provide documentation if needed

**Q: Does leave balance expire?**
A: Depends on policy:
- Some expire Dec 31
- Some carry over (with limits)
- Check your company's policy`,
          },
        ],
      },
      {
        id: 'overtime',
        icon: 'Zap',
        title: 'Overtime Requests',
        description: 'Request and track overtime work hours',
        articles: [
          {
            id: 'overtime-basics',
            title: 'Understanding Overtime',
            tags: ['overtime', 'compensation'],
            content: `# Overtime Basics

## What is Overtime?

Overtime is work performed beyond your regular working hours.

### Standard Working Hours
- Typical: 8 hours/day, 40 hours/week
- Defined in your employment contract
- May vary by country and company
- Examples:
  - Monday-Friday: 9 AM - 5 PM
  - Monday-Thursday: 8 AM - 6 PM
  - Shift work: 7 AM - 3 PM

### When Overtime Applies
**Beyond daily hours:**
- Working past 5 PM if your day ends at 5 PM
- Starting before 9 AM if you start at 9 AM

**Beyond weekly hours:**
- Working more than 40 hours in a week
- Working on scheduled days off

**Special days:**
- Weekends (if not scheduled)
- Public holidays
- Company rest days

## Types of Overtime

### Pre-Approved Overtime
- Planned in advance
- Request submitted before working
- Manager approves beforehand
- Scheduled overtime
- **Examples:**
  - Project deadlines
  - Special events
  - Seasonal busy periods
  - Scheduled maintenance

### Emergency Overtime
- Unplanned, urgent work
- Submit request immediately
- Manager approves after the fact
- Must justify emergency
- **Examples:**
  - System outages
  - Client emergencies
  - Critical deadlines
  - Urgent fixes

### Compensatory Overtime
- Work on rest days/holidays
- Usually higher compensation rate
- Requires special approval
- May offer time-off in lieu
- **Examples:**
  - Public holiday work
  - Weekend projects
  - Called in during vacation
  - Emergency coverage

## Overtime Compensation

### Compensation Methods

**1. Overtime Pay (Most Common)**
- Paid at higher hourly rate
- Example rates:
  - Weekday overtime: 1.5x regular rate
  - Weekend/holiday: 2.0x regular rate
  - Late night: 1.75x regular rate
- Added to monthly salary
- Taxable income

**2. Time Off In Lieu (TOIL)**
- Extra time off instead of pay
- 1:1 or enhanced ratio
- Example: 2 hours overtime = 3 hours off
- Must be used within timeframe
- Subject to approval

**3. Fixed Overtime Allowance**
- Monthly fixed amount
- Regardless of actual hours
- Part of salary package
- No hourly tracking
- Common for managers

### Calculation Example

**Your hourly rate: $20/hour**
**Overtime rate: 1.5x = $30/hour**

**Scenario:**
- Regular shift: 9 AM - 5 PM (8 hours)
- Worked until: 8 PM (11 hours total)
- Overtime: 3 hours
- **Overtime pay: 3 × $30 = $90**

## Overtime Limits

### Legal Limits
Different countries have different laws:
- **UAE:** Max 2 hours/day overtime
- **USA:** Varies by state, typically no daily limit
- **EU:** 48 hours/week including overtime
- Check your local labor law

### Company Limits
Your company may set:
- Maximum overtime hours per day
- Maximum per week
- Maximum per month
- Approval required beyond limits

### Health & Safety
⚠️ **Important:**
- Excessive overtime affects health
- Reduces productivity
- Increases errors
- Can lead to burnout
- Mandatory rest periods required

## Overtime Policies

### Eligibility
Not all employees are eligible:

**Eligible:**
- Hourly employees
- Non-exempt workers
- Field staff
- Operations teams
- Technical staff

**Not Eligible:**
- Senior management
- Employees on fixed overtime allowance
- Certain salaried positions
- Contractors (depends on contract)

### Approval Requirements

**Who Can Approve:**
- Direct manager/supervisor
- Department head
- HR (for excessive overtime)
- Senior management (holidays/weekends)

**Approval Factors:**
- Business necessity
- Budget availability
- Employee's regular hours
- Department workload
- Compliance with limits

### Documentation Requirements
For overtime to be paid/compensated:
- Must be pre-approved (except emergencies)
- Actual hours must be recorded
- Reason must be documented
- Work output may need verification
- Clock-in/out records must match

## Overtime vs Regular Hours

### Key Differences

**Regular Hours:**
- Scheduled work time
- Standard pay rate
- No special approval needed
- Counted toward weekly target
- Expected productivity

**Overtime Hours:**
- Beyond regular schedule
- Enhanced pay rate
- Requires approval
- Extra to weekly target
- Voluntary (usually)

### Mandatory vs Voluntary

**Mandatory Overtime:**
- Required by company
- Cannot refuse (usually)
- Common in emergencies
- Must still be approved
- Must comply with limits

**Voluntary Overtime:**
- Optional for employee
- Can accept or decline
- Based on availability
- Helps with extra income
- Subject to approval

## Benefits of Overtime

### For Employees
✅ Extra income
✅ Demonstrates commitment
✅ Builds skills
✅ Career advancement
✅ Team support
✅ Project completion

### For Company
✅ Meets deadlines
✅ Handles peak periods
✅ Covers staff shortages
✅ Completes urgent work
✅ Maintains service levels

## Risks of Excessive Overtime

### Health Risks
❌ Fatigue
❌ Stress
❌ Sleep deprivation
❌ Weakened immunity
❌ Mental exhaustion
❌ Work-life imbalance

### Performance Issues
❌ Decreased productivity
❌ More errors
❌ Poor decisions
❌ Reduced quality
❌ Safety incidents

### When to Say No
It's okay to decline overtime if:
- You're exhausted
- Personal commitments exist
- Health is affected
- Already worked excessive hours
- Unsafe to continue

**How to decline politely:**
"I appreciate the opportunity, but I have prior commitments and need to maintain my work-life balance. Can we explore other solutions?"

## Overtime Best Practices

### For Employees
✅ Submit requests early when possible
✅ Track your actual overtime hours
✅ Don't exaggerate hours worked
✅ Document the work performed
✅ Maintain quality even during overtime
✅ Take breaks during long shifts
✅ Know your rights and limits
✅ Balance overtime with rest

### For Managers
✅ Plan to minimize overtime
✅ Approve based on real need
✅ Monitor employee overtime hours
✅ Ensure fair distribution
✅ Watch for burnout signs
✅ Verify work was actually performed
✅ Process approvals promptly
✅ Follow company policies

## Common Misconceptions

**Myth:** "Any extra work is overtime"
**Reality:** Only work beyond scheduled hours, with approval

**Myth:** "Overtime is always paid"
**Reality:** Depends on your contract and role

**Myth:** "I must accept all overtime"
**Reality:** Usually voluntary unless contract states otherwise

**Myth:** "Overtime looks good for promotion"
**Reality:** Quality of work matters more than hours

**Myth:** "I can claim overtime without approval"
**Reality:** Must be pre-approved (except emergencies)

## Important Reminders

📌 Always get approval BEFORE working overtime
📌 Record actual hours accurately
📌 Know your company's overtime policy
📌 Understand your rights under local law
📌 Don't burn out - rest is important
📌 Track overtime on every payslip
📌 Report any unpaid overtime to HR
📌 Emergency overtime still needs documentation`,
          },
          {
            id: 'request-overtime',
            title: 'How to Request Overtime',
            tags: ['overtime', 'how-to', 'process'],
            content: `# Requesting Overtime

## Before Requesting Overtime

### Verify Eligibility
✅ Check if you're eligible for overtime
✅ Review your employment contract
✅ Confirm with HR if unsure
✅ Check company overtime policy

### Confirm Business Need
✅ Is the work truly necessary?
✅ Can it be done during regular hours?
✅ Is there a deadline?
✅ Have you exhausted regular hours?
✅ Is overtime the best solution?

### Check Limits
✅ How much overtime you've already worked
✅ Daily/weekly/monthly limits
✅ Budget availability
✅ Manager's approval capacity

## Submitting Overtime Request

### Step-by-Step Process

**1. Navigate to Overtime Requests**
- Click **"Overtime Requests"** in sidebar
- Or from Dashboard quick links

**2. Click "New Overtime Request"**
- Big button at top of page
- Opens request form

**3. Select Overtime Type**

Choose from:

**Planned Overtime**
- Known in advance
- Scheduled project work
- Submit before the work
- Easier to approve

**Emergency Overtime**
- Urgent, unplanned
- Critical situations
- Can submit during/after work
- Needs strong justification

**Weekend Overtime**
- Work on Saturday/Sunday
- Higher compensation rate
- Needs advance approval
- Special category

**Holiday Overtime**
- Work on public holiday
- Highest compensation rate
- Requires senior approval
- Exceptional circumstances

**4. Enter Date and Time Details**

**Date***
- Select the date of overtime work
- Can be future (planned) or past (emergency)

**Start Time***
- When overtime begins
- Usually after regular hours end
- Example: 6:00 PM if day ends at 5 PM

**End Time***
- When overtime will end
- Be realistic
- Include breaks if long shift

**Total Hours**
- Auto-calculated
- Shows overtime duration
- Verify accuracy

**5. Provide Justification***

**Reason for Overtime** (Required)
- Clear, concise explanation
- Business justification
- Why it can't wait

**Good examples:**
- "Client presentation deadline tomorrow, need to finalize slides"
- "Production server down, must restore service"
- "Month-end financial close, process payroll"
- "Urgent customer request for project delivery"

**Poor examples:**
- "Need extra money"
- "Want to finish work"
- "Nothing else to do"
- "Manager said so" (manager should approve, but need work reason)

**6. Add Work Details**

**Tasks to be Performed**
- List specific tasks
- Helps verify work done
- Used for approval decision

Example:
- Update quarterly reports
- Fix critical bug in production
- Complete client deliverables
- Attend international conference call

**Expected Deliverables**
- What will be completed?
- Helps measure productivity
- Evidence for payment

Example:
- Completed financial report
- Bug fixed and deployed
- Presentation ready
- Meeting minutes documented

**7. Select Compensation Type**

If company offers choice:

**Overtime Pay**
- Money added to salary
- At enhanced rate (1.5x, 2x)
- Taxable
- Paid with next salary

**Time Off In Lieu (TOIL)**
- Compensatory time off
- Usually 1:1 or enhanced ratio
- Must use within specified period
- Subject to approval when using

**8. Upload Supporting Documents** (if required)

Examples:
- Email showing client deadline
- Project timeline
- System alert screenshots
- Meeting invitations
- Manager's email approval

**File Requirements:**
- Formats: PDF, JPG, PNG, DOC
- Max 5 files
- Max 5MB per file

**9. Review and Submit**

**Final Check:**
- Date and time correct?
- Hours calculated properly?
- Justification clear?
- All required fields filled?
- Supporting docs uploaded?

**Submit:**
- Click **"Submit Overtime Request"**
- Confirmation appears
- Notification sent to manager
- Email sent to you

## After Submission

### Request Status Tracking

**Pending** 🟡
- Submitted, awaiting approval
- Manager reviewing
- Can still edit/cancel
- Typical wait: 24-48 hours

**Approved** 🟢
- Manager approved
- Proceed with overtime work
- Record actual hours
- Will be compensated

**Rejected** 🔴
- Manager declined
- Reason in comments
- Do not work overtime
- Can submit new request with changes

**Completed** ✅
- Overtime worked and verified
- Awaiting compensation
- Will appear in next payslip

### Notifications
You'll be notified:
- Request submitted ✅
- Manager viewed 👀
- Request approved ✅
- Request rejected ❌
- Comments added 💬
- Payment processed 💰

### What to Do After Approval

**1. Perform the Overtime Work**
- Work the approved hours
- Complete stated tasks
- Track your actual time
- Maintain quality

**2. Clock Your Hours**
- Clock in when starting overtime
- Clock out when finishing
- System tracks actual duration
- Must match approved hours

**3. Submit Work Evidence** (if required)
- Upload completed deliverables
- Provide work summary
- Screenshots of completed tasks
- Any outputs produced

**4. Confirm Actual Hours**
- If different from approved
- Explain variation
- Manager verifies
- Adjustment made if needed

## Emergency Overtime Requests

### When You Can't Request in Advance

**Scenario:** System crash at 4:30 PM, you stay until 9 PM

**Steps:**
1. **Notify Manager Immediately**
   - Call or message
   - "System down, working overtime to fix"
   - Don't wait until next day

2. **Clock Your Overtime**
   - Clock in/out as normal
   - System records actual hours
   - Evidence of work

3. **Submit Request ASAP**
   - Same day if possible
   - Next morning at latest
   - Mark as "Emergency Overtime"

4. **Provide Full Explanation**
   - What was the emergency?
   - Why overtime was necessary?
   - What was accomplished?
   - Include evidence (emails, tickets, etc.)

5. **Manager Retroactive Approval**
   - They approve after the fact
   - Verifies emergency was genuine
   - Confirms hours worked
   - Processes payment

## Multiple Consecutive Overtime

### Working Overtime Multiple Days

**Example:** Major project, need overtime Monday-Friday

**Option 1: Single Request for All Days**
- Submit one request
- List all dates
- Total hours for week
- Single approval for all

**Option 2: Daily Requests**
- Submit separately each day
- If hours vary
- More detailed tracking
- Individual approvals

**Recommended:** Option 1 for efficiency

### Extended Overtime Projects

For long-term overtime (weeks/months):

1. **Submit Master Request**
- Overall project scope
- Estimated total hours
- Duration of project
- High-level approval

2. **Weekly Time Sheets**
- Track actual hours weekly
- Submit for verification
- Running total
- Payment per pay period

3. **Regular Reviews**
- Weekly check-ins
- Adjust if needed
- Ensure not exceeding limits
- Monitor health/productivity

## Modifying Overtime Requests

### Before Approval

**Edit Request:**
1. Go to Overtime Requests
2. Find pending request
3. Click "Edit"
4. Make changes
5. Resubmit
6. Approval process restarts

**Cancel Request:**
1. Find pending request
2. Click "Cancel"
3. Confirm cancellation
4. Request withdrawn
5. Can submit new one

### After Approval

**Cannot Edit Approved Requests**

If you need changes:
1. Contact manager
2. Explain situation
3. They can modify on admin side
4. Or cancel and create new request

**If You Worked Different Hours:**
1. Submit actual hours when clocking
2. Note discrepancy in comments
3. Manager verifies actual work
4. Compensation adjusted accordingly

## Common Overtime Scenarios

### Scenario 1: Project Deadline
**Situation:** Major deliverable due Friday, need extra hours

**Solution:**
- Monday: Submit overtime request for Mon-Thu evenings
- Justify: Project deadline Friday
- Specify: 2 hours each evening (total 8 hours)
- Attach: Project timeline, manager email
- Work approved hours
- Submit completed work as evidence

### Scenario 2: System Outage
**Situation:** Server crash at 5 PM, fixed by 10 PM

**Solution:**
- 5 PM: Notify manager of issue
- Work to fix (5 PM - 10 PM = 5 hours)
- Clock actual overtime hours
- 10 PM or next morning: Submit emergency overtime request
- Attach: Incident ticket, resolution proof
- Manager approves retroactively

### Scenario 3: Weekend Deployment
**Situation:** Software deployment requires Saturday work

**Solution:**
- Week before: Submit weekend overtime request
- Specify: Saturday 9 AM - 5 PM (8 hours)
- Justify: Production deployment window
- Select: Weekend overtime (higher rate)
- Attach: Deployment plan
- Get approval before weekend
- Work on Saturday
- Submit deployment report after

### Scenario 4: International Call
**Situation:** Client call at 9 PM due to time zones

**Solution:**
- Submit overtime for specific hours (9 PM - 11 PM)
- Justify: Client in different timezone
- Attach: Meeting invitation
- 2 hours overtime
- Simple, easy approval

## Tracking Your Overtime

### Personal Records

**Keep Track Of:**
- All overtime requests submitted
- Approval dates
- Actual hours worked
- Expected compensation
- Payment received

**Why?**
- Verify accurate payment
- Track total overtime
- Support any disputes
- Personal productivity records
- Tax documentation

### Monthly Overtime Summary

**View Your Stats:**
1. Go to Overtime Requests
2. Click "Monthly Summary"
3. See:
   - Total overtime hours
   - Total compensation
   - By type (weekday/weekend/holiday)
   - Approval rate
   - Pending requests

### Payslip Verification

**Each Month:**
1. Check payslip
2. Verify overtime hours listed
3. Confirm compensation rate
4. Calculate expected amount
5. Compare with payment
6. Report discrepancies to HR immediately

## Best Practices

### DO ✅
✅ Request in advance whenever possible
✅ Provide clear, honest justification
✅ Work only approved hours
✅ Clock in/out for overtime
✅ Submit work evidence
✅ Track all overtime
✅ Verify payment
✅ Communicate with manager
✅ Know your limits
✅ Maintain work quality

### DON'T ❌
❌ Work overtime without approval
❌ Exaggerate hours worked
❌ Claim overtime for regular work
❌ Abuse emergency category
❌ Exceed legal/company limits
❌ Ignore health warnings
❌ Assume automatic approval
❌ Submit false justifications
❌ Work and hope for approval later (non-emergencies)
❌ Forget to track hours

## Red Flags & Issues

### Request Repeatedly Rejected
**Possible reasons:**
- Insufficient justification
- Work not urgent
- Budget constraints
- Already excessive overtime
- Can be done in regular hours

**Action:**
- Discuss with manager
- Understand concerns
- Adjust work approach
- Plan better
- Consider alternatives

### Approved But Not Paid
**If overtime doesn't appear on payslip:**
1. Check overtime request status
2. Verify approval date
3. Check payroll cutoff dates
4. Contact HR with request details
5. Provide approval confirmation
6. Follow up until resolved

### Worked Overtime Without Approval
**If you forgot to request:**
- Submit emergency request immediately
- Explain oversight
- Provide work evidence
- Hope for understanding
- Payment not guaranteed
- Don't repeat mistake

**Prevention:**
- Always submit first
- Set calendar reminders
- Check approvals before working
- Communicate with manager

## FAQs

**Q: How long does approval take?**
A: Usually 24-48 hours for normal requests, faster for emergencies.

**Q: Can I request overtime retroactively?**
A: Only for genuine emergencies. Planned work must be pre-approved.

**Q: What if I work more/less than approved?**
A: Update actual hours when clocking out. Manager verifies. Payment adjusted.

**Q: Is overtime mandatory?**
A: Usually voluntary unless contract states otherwise or genuine emergency.

**Q: Can overtime request be partially approved?**
A: Yes, manager can approve fewer hours than requested.

**Q: What if manager doesn't respond?**
A: Follow up after 2 days. Escalate to their manager or HR if urgent.

**Q: Can I cancel approved overtime?**
A: Yes, if you haven't worked yet. Inform manager and cancel request.

**Q: Is there a limit to overtime?**
A: Yes, company and legal limits apply. Check your policy.

**Q: When is overtime paid?**
A: Usually next payroll cycle after approval and verification.

**Q: Can I choose between pay and time off?**
A: Depends on company policy. Some offer choice, others don't.`,
          },
        ],
      },
      {
        id: 'profile',
        icon: 'User',
        title: 'Profile & Settings',
        description: 'Manage your personal information and preferences',
        articles: [
          {
            id: 'update-profile',
            title: 'Updating Your Profile Information',
            tags: ['profile', 'settings', 'personal'],
            content: `# Managing Your Profile

## Accessing Your Profile

### From Any Page
1. Click your **name/avatar** in top right corner
2. Select **"Profile"** from dropdown
3. OR click **"Profile"** in left sidebar

## Profile Sections

### Personal Information

**Basic Details (View Only):**
- Full Name
- Employee ID/Code
- Email Address
- Date of Joining

*These fields are set by HR and cannot be self-edited*

**Editable Details:**
- Phone Number
- Personal Email (different from work email)
- Address
- Emergency Contact Information

**How to Update:**
1. Go to Profile page
2. Click **"Edit Profile"** button
3. Modify editable fields
4. Click **"Save Changes"**
5. Confirmation appears

### Contact Information

**Work Email**
- Your official company email
- Used for system notifications
- Cannot be changed (contact HR)

**Phone Number**
- Mobile/WhatsApp number
- For urgent contact
- SMS notifications (if enabled)
- Keep updated

**Personal Email** (Optional)
- Backup contact email
- Personal notifications
- Account recovery
- Not for official communication

**Address**
- Current residence
- For emergency contact
- HR/payroll purposes
- Keep current if relocated

### Emergency Contact

**Why It's Important:**
- Medical emergencies
- Accidents at work
- Unable to reach you
- Family notifications

**Required Information:**
- Emergency Contact Name*
  - Full name
  - Relationship (spouse, parent, sibling, friend)

- Emergency Contact Phone*
  - Primary number
  - Should be reachable anytime
  - Keep updated

- Alternative Contact (Optional)
  - Backup person
  - Secondary phone

**Updating Emergency Contact:**
1. Profile → Emergency Contact section
2. Click "Edit"
3. Enter/update details
4. Save changes
5. Verify information is correct

### Work Information (View Only)

You can view but not edit:
- Department
- Position/Title
- Manager Name
- Work Location
- Employee Status
- Hire Date
- Employment Type (Full-time/Part-time)

*To change work information, contact HR*

### Profile Photo

**Adding/Changing Photo:**
1. Profile → Photo section
2. Click **"Upload Photo"** or click on current photo
3. Select image from device
4. Crop/adjust if needed
5. Click "Save"

**Photo Requirements:**
- File types: JPG, PNG
- Maximum size: 5 MB
- Minimum resolution: 200x200 pixels
- Recommended: 400x400 pixels or higher
- Professional headshot recommended
- Clear face visibility
- Appropriate attire

**Photo Usage:**
- Shown on your profile
- Appears in team directory
- Shows in approval workflows
- Attendance photo comparison
- Internal identification

**Removing Photo:**
1. Click on current photo
2. Click "Remove Photo"
3. Confirm removal
4. Default avatar shown

### Work Schedule (View Only)

View your assigned work schedule:
- Working Days (e.g., Monday-Friday)
- Start Time (e.g., 9:00 AM)
- End Time (e.g., 5:00 PM)
- Break Times
- Total Hours per Day
- Total Hours per Week

*To request schedule changes, contact your manager and HR*

### Department & Reporting

**Your Department:**
- Department name
- Department head
- Team members (if visible)

**Reporting Structure:**
- Direct Manager
- Manager's email
- Manager's phone
- Department Head

*Organizational chart may be available*

## Account Settings

### Password Management

**Changing Your Password:**
1. Profile → Security tab
2. Click **"Change Password"**
3. Enter current password
4. Enter new password
5. Confirm new password
6. Click "Update Password"

**Password Requirements:**
- Minimum 8 characters
- At least one uppercase letter
- At least one lowercase letter
- At least one number
- At least one special character (!@#$%^&*)
- Cannot be same as last 3 passwords
- Cannot contain your name or email

**Password Tips:**
✅ Use unique password (not used elsewhere)
✅ Use passphrase (e.g., "MyDog@Coffee2024!")
✅ Use password manager
✅ Change regularly (every 3-6 months)
✅ Don't share with anyone
❌ Don't write it down
❌ Don't use common words
❌ Don't use personal info (birthday, etc.)

**Forgot Password?**
1. Go to login page
2. Click "Forgot Password"
3. Enter your email
4. Check email for reset link
5. Click link (valid 24 hours)
6. Create new password
7. Login with new password

### Notification Preferences

**Choose What Notifications You Receive:**

**Email Notifications:**
- [ ] Attendance approvals
- [ ] Leave request status
- [ ] Overtime approvals
- [ ] Schedule changes
- [ ] Policy updates
- [ ] Payroll notifications
- [ ] System announcements

**In-App Notifications:**
- [ ] Real-time alerts
- [ ] Manager messages
- [ ] Task assignments
- [ ] Deadline reminders
- [ ] Team updates

**SMS Notifications** (if available):
- [ ] Critical alerts only
- [ ] Attendance reminders
- [ ] Emergency notifications

**How to Update:**
1. Profile → Settings → Notifications
2. Toggle checkboxes for each type
3. Click "Save Preferences"
4. Confirmation appears

### Language & Region

**Language Preference:**
- English (Default)
- Arabic
- Other supported languages

**Changing Language:**
1. Profile → Settings → Language
2. Select preferred language
3. Save changes
4. Interface updates immediately

**Date Format:**
- DD/MM/YYYY (European)
- MM/DD/YYYY (US)
- YYYY-MM-DD (ISO)

**Time Format:**
- 12-hour (AM/PM)
- 24-hour

**Timezone:**
- Auto-detected based on location
- Can manually select if needed
- Important for accurate time tracking

### Privacy Settings

**Profile Visibility:**
- Who can see your profile
- Options:
  - Everyone in company
  - Same department only
  - Managers only
  - Private (HR and managers only)

**Contact Information Sharing:**
- Show phone number to colleagues
- Show personal email
- Show in company directory

**Attendance Visibility:**
- Who can see your attendance
- Usually limited to managers and HR
- Cannot be changed by employee

## Viewing Your Information

### Personal Dashboard
- Profile completion percentage
- Last profile update
- Account created date
- Last login time
- Active sessions

### Employment Details
- Contract type
- Probation status (if applicable)
- Tenure with company
- Benefits enrollment
- Payroll information link

### Documents (if available)
- Employment contract
- Offer letter
- ID documents
- Certificates
- Payslips
- Tax documents

*Document access varies by company*

## Data Privacy & Security

### Your Data Rights
- Access your personal data
- Request corrections
- Export your data
- Request deletion (subject to legal requirements)
- Know how data is used

### Data Protection
- All personal data encrypted
- Access logged and monitored
- Only authorized personnel can view
- Compliant with data protection laws
- Regular security audits

### Reporting Privacy Concerns
If you suspect data misuse:
1. Contact HR immediately
2. File formal complaint
3. Escalate to management
4. External authorities if needed

## Best Practices

### Keep Your Profile Updated
✅ Review profile monthly
✅ Update phone number when changed
✅ Update address when relocated
✅ Verify emergency contact regularly
✅ Keep photo current and professional
✅ Maintain accurate information

### Profile Completion
**Complete profiles help with:**
- Easier internal communication
- Accurate emergency response
- Better team collaboration
- HR processes
- Compliance requirements

**Aim for 100% completion**

### Security Tips
✅ Strong, unique password
✅ Change password regularly
✅ Enable two-factor authentication (if available)
✅ Don't share credentials
✅ Review active sessions
✅ Log out from shared devices
✅ Report suspicious activity

### Common Issues

**Can't Update Certain Fields**
- Some fields locked by HR
- Contact HR to change:
  - Name
  - Email
  - Employee ID
  - Department
  - Position
  - Salary

**Changes Not Saving**
- Check required fields filled
- Verify data format (phone number, etc.)
- Check file size (for photos)
- Try different browser
- Clear cache
- Contact IT support

**Forgot Password**
- Use "Forgot Password" link
- Check email (including spam)
- Reset link valid 24 hours
- Contact IT if email not received
- May need admin reset

**Profile Photo Won't Upload**
- Check file size (< 5MB)
- Check file format (JPG/PNG only)
- Try resizing image
- Use different browser
- Check internet connection
- Contact IT support

## FAQs

**Q: How often should I update my profile?**
A: Review at least quarterly. Update immediately if contact info changes.

**Q: Can colleagues see my personal information?**
A: Depends on privacy settings. Usually only basic info visible to colleagues.

**Q: Can I have multiple phone numbers?**
A: One primary number required. Can add alternative numbers in some systems.

**Q: What if my manager information is wrong?**
A: Contact HR - they manage reporting structures.

**Q: Can I delete my account?**
A: No. Employment accounts managed by HR. Deactivated upon termination.

**Q: Is my data secure?**
A: Yes, enterprise-grade security and encryption used.

**Q: Who can see my profile?**
A: Depends on settings. At minimum: you, your manager, and HR.

**Q: Can I export my data?**
A: Usually yes. Request from HR or through profile settings.`,
          },
        ],
      },
      {
        id: 'reports',
        icon: 'Download',
        title: 'Reports & Exports',
        description: 'Generate and download attendance and leave reports',
        articles: [
          {
            id: 'export-reports',
            title: 'Generating and Exporting Reports',
            tags: ['reports', 'export', 'documents'],
            content: `# Reports & Exports

## Available Reports

ExoTrack allows you to generate and export various reports for your records:

### 1. Attendance Report
**Contains:**
- All attendance records
- Clock-in/out times
- Total hours worked
- Break times
- Location data
- Status of each record

**Uses:**
- Personal records
- Timesheet submission
- Dispute resolution
- Personal productivity tracking

### 2. Leave Report
**Contains:**
- All leave requests
- Leave type
- Dates taken
- Approval status
- Leave balance changes
- Approver information

**Uses:**
- Leave tracking
- Balance verification
- Planning future leave
- Annual summaries

### 3. Overtime Report
**Contains:**
- Overtime requests
- Approved hours
- Actual hours worked
- Compensation type
- Approval details
- Payment status

**Uses:**
- Income verification
- Tax documentation
- Hour tracking
- Payment disputes

### 4. Comprehensive Timesheet
**Contains:**
- Combined attendance, leave, and overtime
- Day-by-day breakdown
- Total hours summary
- Leave days marked
- Public holidays
- Complete picture of time

**Uses:**
- Monthly submissions
- Client billing (if applicable)
- Complete time tracking
- Annual reviews

## Generating Reports

### Step-by-Step Process

**1. Navigate to Reports**
Access reports from:
- Dashboard → Export button
- History page → Export
- Leave Requests → Export
- Overtime Requests → Export

**2. Select Report Type**
Choose from dropdown:
- Attendance Report
- Leave Report
- Overtime Report
- Comprehensive Timesheet
- Custom Report

**3. Set Date Range**

**Quick Selections:**
- This Week
- Last Week
- This Month
- Last Month
- This Quarter
- This Year
- Last Year

**Custom Range:**
- Select "Custom"
- Choose Start Date
- Choose End Date
- Click "Apply"

**4. Choose Format**

**PDF Format**
- Professional layout
- Formatted for printing
- Includes charts/graphs
- Company branding
- Best for: Formal submissions, official records

**Excel Format (XLSX)**
- Spreadsheet data
- Editable (locally)
- Pivot tables possible
- Calculations possible
- Best for: Data analysis, custom reports

**CSV Format**
- Raw data
- Import to other systems
- Universal format
- No formatting
- Best for: Data transfer, integration

**5. Select Options**

**Include:**
- [ ] Summary statistics
- [ ] Charts and graphs
- [ ] Photos (attendance report)
- [ ] Location maps
- [ ] Approver signatures
- [ ] Company header
- [ ] Page numbers
- [ ] Date of generation

**Filters:**
- [ ] Approved records only
- [ ] Exclude pending
- [ ] Include only worked days
- [ ] Show breaks separately

**6. Generate Report**
- Click **"Generate Report"** button
- Processing message appears
- May take few seconds for large reports
- Download begins automatically

**7. Save Report**
- Choose save location on device
- Rename if desired
- Open immediately or save for later

## Report Contents

### Attendance Report Details

**Header Section:**
- Your name
- Employee ID
- Department
- Report period
- Generation date

**Summary Section:**
- Total days worked
- Total hours
- Average hours/day
- Late arrivals
- Early departures
- Absences

**Detailed Records Table:**
Date | Day | Clock In | Clock Out | Duration | Breaks | Location | Status
-----|-----|----------|-----------|----------|--------|----------|-------

**Footer:**
- Grand totals
- Report signature area (if printing)

**Charts (if selected):**
- Daily hours bar chart
- Weekly trends
- Comparison to standard hours

### Leave Report Details

**Header Section:**
- Your details
- Current leave balances
- Report period

**Balance Summary:**
- Opening balance
- Leave accrued
- Leave taken
- Leave pending
- Closing balance
- Per leave type breakdown

**Requests Table:**
Request Date | Leave Type | From Date | To Date | Days | Status | Approver | Notes
-------------|------------|-----------|---------|------|--------|----------|------

**Analysis:**
- Most used leave type
- Average request duration
- Approval rate
- Leave patterns

### Overtime Report Details

**Header:**
- Your information
- Report period
- Total overtime summary

**Summary:**
- Total overtime hours
- Total compensation
- Breakdown by type (weekday/weekend/holiday)
- Average per request

**Requests Table:**
Date | Type | Hours Requested | Hours Approved | Hours Worked | Rate | Compensation | Status
-----|------|----------------|----------------|--------------|------|--------------|-------

**Totals:**
- Hours by category
- Total compensation
- Payment status

### Comprehensive Timesheet

**Calendar View:**
Day-by-day grid showing:
- Working days: Hours worked
- Leave days: Leave type
- Holidays: Marked
- Weekends: Greyed out
- Overtime: Highlighted

**Daily Details:**
- Clock in/out for worked days
- Leave type for leave days
- Overtime hours
- Notes/comments

**Weekly Summaries:**
- Total hours per week
- Leave days
- Overtime hours
- Week numbers

**Monthly Summary:**
- Total working days
- Days attended
- Days on leave
- Total hours
- Total overtime
- Attendance percentage

## Using Exported Reports

### For Personal Records
- Keep monthly backups
- Organize by month/year
- Store securely
- Cloud backup recommended

### For Official Submissions
- If required by management
- Timesheet approvals
- Client billing
- Project reporting

### For Disputes
- Evidence of attendance
- Proof of leave
- Overtime verification
- Payment disputes

### For Tax Documentation
- Annual income records
- Overtime earnings
- Leave encashment
- Deductions

### For Visa/Immigration
- Proof of employment
- Attendance records
- Leave history
- Official documentation

## Scheduling Automated Reports

**If Your Company Offers This Feature:**

**Setup Recurring Reports:**
1. Go to Reports → Schedule
2. Select report type
3. Choose frequency:
   - Daily
   - Weekly (Monday mornings)
   - Bi-weekly
   - Monthly (1st of month)
4. Select format
5. Enter email address
6. Save schedule

**Benefits:**
- Automatic record keeping
- Never forget to export
- Regular backups
- Consistent documentation

**Managing Schedules:**
- View active schedules
- Edit frequency
- Pause temporarily
- Delete schedule

## Report Security

### Data Privacy
- Reports contain personal data
- Store securely
- Don't share publicly
- Password-protect if emailing

### Secure Storage
✅ Use password-protected folders
✅ Cloud storage with encryption
✅ Company-provided secure storage
✅ Regular backups

❌ Don't store on public/shared computers
❌ Don't email without encryption
❌ Don't leave printed copies unsecured
❌ Don't share credentials to access reports

### Sharing Reports

**When Required to Share:**
- Send only to authorized recipients
- Use secure email (encryption)
- Password-protect files
- Confirm recipient received
- Use company-approved methods

**When NOT to Share:**
- Personal curiosity
- Unofficial requests
- Social media
- Public forums
- Unauthorized persons

## Troubleshooting

### Report Won't Generate
**Possible causes:**
- Date range too large
- No data for selected period
- Server busy
- Browser issues

**Solutions:**
1. Try smaller date range
2. Check if data exists for period
3. Wait and try again
4. Use different browser
5. Clear cache
6. Contact IT support

### Download Failed
**Solutions:**
- Check internet connection
- Disable popup blockers
- Try different browser
- Generate again
- Check download folder
- Check storage space

### Report Data Looks Wrong
**Verify:**
- Correct date range selected
- Right filters applied
- Data actually exists
- Compare with online records
- Check report type

**If Still Wrong:**
- Screenshot the issue
- Note discrepancies
- Contact HR or IT
- Provide report details
- May indicate system issue

### Can't Open Downloaded File
**PDF Issues:**
- Install PDF reader (Adobe, Foxit, etc.)
- Update existing reader
- Try opening in browser
- Try different device

**Excel Issues:**
- Install Microsoft Excel or compatible app
- Use Google Sheets online
- Try LibreOffice (free)
- Open in Excel Online

**CSV Issues:**
- Open with Excel or Google Sheets
- Text editor shows raw data
- Import into other applications

## Best Practices

### Regular Exports
✅ Export monthly for records
✅ Export before year-end
✅ Export before leaving company
✅ Keep 2-3 years of history
✅ Backup to multiple locations

### Verify Accuracy
✅ Review reports after generation
✅ Compare with online data
✅ Check totals make sense
✅ Verify dates are correct
✅ Ensure nothing missing

### Organization
✅ Name files clearly:
   - "Attendance_2024_January_John.pdf"
   - "Leave_Report_Q1_2024.xlsx"
   - "Timesheet_March_2024.pdf"
✅ Create folder structure:
   - 2024/
     - January/
     - February/
     - ...
✅ Regular backups
✅ Cloud + local storage

### Professional Use
✅ Use company templates
✅ Include all required sections
✅ Professional formatting
✅ Accurate data only
✅ Proper authorization
✅ Secure transmission

## Advanced Features

### Custom Reports
Some systems allow custom reports:
- Select specific columns
- Apply complex filters
- Group by categories
- Calculate custom metrics
- Create your own layouts

### Report Templates
- Save commonly used configurations
- Reuse monthly
- Share with teammates (if allowed)
- Consistent formatting

### Bulk Export
- Export all employees (if manager)
- Team reports
- Department summaries
- Comparison reports

### Integration
- Export to payroll system
- Import to project management tools
- Integration with HR systems
- API access (advanced users)

## FAQs

**Q: How far back can I generate reports?**
A: Usually your entire employment history is available.

**Q: Are reports real-time or delayed?**
A: Reports reflect data at time of generation. Most recent updates included.

**Q: Can I edit reports after export?**
A: You can edit locally downloaded files, but doesn't change system data. Official reports should not be altered.

**Q: Do reports show pending requests?**
A: Yes, usually marked as "Pending" with option to exclude.

**Q: Can I schedule reports to auto-email?**
A: Depends on system configuration. Check Settings or contact IT.

**Q: Are reports legally valid documents?**
A: Generated reports can serve as official records but may need HR certification for legal purposes.

**Q: Can I export reports for terminated employees?**
A: If you're a terminated employee, request final reports from HR before last day.

**Q: What if report data doesn't match my payslip?**
A: Contact HR immediately with both documents for comparison and correction.

**Q: Can I get reports in other languages?**
A: Depends on system. Check language settings or request from HR.

**Q: Is there a limit to how many reports I can generate?**
A: Usually no limit for personal reports. Excessive usage may be monitored.`,
          },
        ],
      },
    ];
  }

  /**
   * Get FAQs data
   */
  getFAQsData() {
    return [
      // Attendance FAQs
      {
        id: 'faq-1',
        category: 'attendance',
        question: 'What if I forget to clock out?',
        answer: 'If you forget to clock out, contact your supervisor or manager as soon as possible. They can manually adjust your attendance record with the actual clock-out time. Your clock-in record will show as "In Progress" until corrected. To avoid this, set a reminder before leaving work.',
      },
      {
        id: 'faq-2',
        category: 'attendance',
        question: 'Can I clock in from home?',
        answer: 'This depends on your company policy and your role. If you\'re not approved for remote work or field work, the system may prevent clock-in from non-approved locations. If you need to work from home, ensure you have prior approval and select "Field Work" or "Remote Work" option when clocking in.',
      },
      {
        id: 'faq-3',
        category: 'attendance',
        question: 'Why does the system need my photo when clocking in?',
        answer: 'Photos are used for verification purposes to ensure the person clocking in is actually you. This prevents buddy punching (someone else clocking in for you) and maintains the integrity of attendance records. Photos are securely stored and only accessible to authorized personnel.',
      },
      {
        id: 'faq-4',
        category: 'attendance',
        question: 'What if my location is not detected correctly?',
        answer: 'Location accuracy depends on GPS signal. If you\'re indoors or in areas with poor GPS signal, try moving closer to a window or going outside briefly. If you\'re at an approved location but system shows otherwise, contact your supervisor - they can manually approve your attendance.',
      },
      {
        id: 'faq-5',
        category: 'attendance',
        question: 'How long should my work sessions be?',
        answer: 'This depends on your employment contract and company policy. Standard full-time is typically 8 hours per day, 40 hours per week. Check your contract for specifics. The system tracks all your hours, and going significantly over may require overtime approval.',
      },
      {
        id: 'faq-6',
        category: 'attendance',
        question: 'Can I clock in early or stay late?',
        answer: 'You can clock in early, but whether it counts toward working hours depends on company policy. Some companies only count hours within scheduled times unless overtime is pre-approved. For staying late, check if you need overtime approval first.',
      },

      // Leave FAQs
      {
        id: 'faq-7',
        category: 'leave',
        question: 'How many leave days am I entitled to?',
        answer: 'Leave entitlement varies by country, company, and your employment contract. Check your Dashboard leave balance or employment contract. Common allocations: 20-30 days annual leave per year, 10-15 sick leave days, and limited emergency leave. Contact HR for your specific entitlement.',
      },
      {
        id: 'faq-8',
        category: 'leave',
        question: 'What happens to unused leave days?',
        answer: 'This depends on company policy. Common options: (1) Use-it-or-lose-it: expires Dec 31, (2) Carry over limited days to next year, (3) Full carry over with maximum cap, or (4) Paid buy-back for unused days. Check your company\'s specific policy with HR.',
      },
      {
        id: 'faq-9',
        category: 'leave',
        question: 'Can I take leave during my probation period?',
        answer: 'This varies by company. Some allow leave during probation (especially sick leave), while others restrict it. Even if leave accrues during probation, you may not be able to use it until after. Check your employment contract or ask HR about probation leave policies.',
      },
      {
        id: 'faq-10',
        category: 'leave',
        question: 'How much notice do I need to give for leave?',
        answer: 'Typical notice periods: Annual leave requires 2-4 weeks advance notice, sick leave should be reported same day ASAP, emergency leave as soon as possible, and extended leave (5+ days) needs 1-3 months notice. Check company policy for specific requirements and blackout periods.',
      },
      {
        id: 'faq-11',
        category: 'leave',
        question: 'What if my leave request is rejected?',
        answer: 'If rejected, your manager should provide a reason (e.g., busy period, insufficient coverage, too many team members on leave). Review the reason, discuss with your manager for alternative dates, plan better timing, or escalate to HR if you feel the rejection is unfair.',
      },
      {
        id: 'faq-12',
        category: 'leave',
        question: 'Do weekends count as leave days?',
        answer: 'No, weekends are not counted as leave days. If you take leave Friday through Monday, only Friday and Monday count (2 days), not Saturday and Sunday. The system automatically excludes weekends and public holidays from leave calculations.',
      },
      {
        id: 'faq-13',
        category: 'leave',
        question: 'Can I cancel approved leave?',
        answer: 'You cannot cancel approved leave yourself. Contact your manager explaining why you need to cancel. They can cancel it on the admin side if approved. Do this as early as possible before the leave start date. Your leave balance will be restored upon cancellation.',
      },

      // Overtime FAQs
      {
        id: 'faq-14',
        category: 'overtime',
        question: 'How is overtime pay calculated?',
        answer: 'Overtime pay = (Hourly Rate × Overtime Multiplier × Hours). Common multipliers: Weekday overtime 1.5x, Weekend 2.0x, Public holidays 2.5x or 3.0x. Example: If your hourly rate is $20 and you work 3 hours weekday overtime at 1.5x, you earn 3 × $20 × 1.5 = $90.',
      },
      {
        id: 'faq-15',
        category: 'overtime',
        question: 'Is overtime mandatory?',
        answer: 'Usually overtime is voluntary unless: (1) your contract specifies mandatory overtime, (2) genuine emergency situations, (3) business-critical needs. Even if mandatory in contract, there are legal limits. You generally have the right to refuse excessive overtime, especially if affecting health or family.',
      },
      {
        id: 'faq-16',
        category: 'overtime',
        question: 'Can I work overtime without approval?',
        answer: 'NO. Working overtime without approval means payment is not guaranteed. Always request approval before working overtime (except genuine emergencies where you should notify manager immediately and submit request ASAP). Unapproved overtime may not be compensated.',
      },
      {
        id: 'faq-17',
        category: 'overtime',
        question: 'What if I work different hours than approved?',
        answer: 'When clocking out, update the actual hours worked. Add notes explaining the difference (e.g., "Task took longer than expected" or "Finished early"). Your manager will verify the actual work done. Compensation will be adjusted based on verified actual hours.',
      },
      {
        id: 'faq-18',
        category: 'overtime',
        question: 'When will I receive overtime payment?',
        answer: 'Overtime is typically paid in the next payroll cycle after approval and verification. Timeline: (1) Work overtime, (2) Request approved, (3) Hours verified, (4) Processed in payroll, (5) Paid in next salary (usually within 1 month). Check your payslip to verify overtime payments.',
      },

      // Profile FAQs
      {
        id: 'faq-19',
        category: 'profile',
        question: 'How do I change my password?',
        answer: 'Go to Profile → Security → Change Password. Enter current password, then new password twice. Password must be minimum 8 characters with uppercase, lowercase, number, and special character. Use a strong, unique password. Change it regularly every 3-6 months for security.',
      },
      {
        id: 'faq-20',
        category: 'profile',
        question: 'Can I change my name or email address?',
        answer: 'No, you cannot change your name or official email yourself as these are set by HR. If you legally changed your name or need email updated, contact HR with supporting documentation (marriage certificate, legal name change document, etc.). They will update your records.',
      },
      {
        id: 'faq-21',
        category: 'profile',
        question: 'Why should I add emergency contact information?',
        answer: 'Emergency contacts are critical for: workplace accidents or injuries, medical emergencies, being unreachable during emergencies, or urgent family matters. Ensure information is current and the person listed is reachable 24/7. Keep it updated if contact details change.',
      },

      // General FAQs
      {
        id: 'faq-22',
        category: 'general',
        question: 'What browsers are supported?',
        answer: 'ExoTrack works best on modern browsers: Chrome 90+ (recommended), Firefox 88+, Safari 14+, and Edge 90+. Mobile: Chrome Mobile, Safari Mobile, Samsung Internet. Ensure JavaScript is enabled and pop-up blockers allow ExoTrack. Update your browser regularly for best experience.',
      },
      {
        id: 'faq-23',
        category: 'general',
        question: 'Can I use ExoTrack on my phone?',
        answer: 'Yes! ExoTrack is fully mobile-responsive. Use your mobile browser to access the same website. Features like camera (for attendance photos), location (for field work), and notifications work on mobile. Consider adding to home screen for quick access.',
      },
      {
        id: 'faq-24',
        category: 'general',
        question: 'Is my data secure?',
        answer: 'Yes. ExoTrack uses enterprise-grade security: data encrypted in transit and at rest, secure authentication, access controls (only authorized personnel can view your data), regular security audits, and compliance with data protection regulations (GDPR, etc.). Never share your login credentials.',
      },
      {
        id: 'faq-25',
        category: 'general',
        question: 'Who can I contact for help?',
        answer: 'For technical issues: Contact IT Support. For attendance/leave policy questions: Contact HR. For approval-related questions: Contact your direct manager. For system access issues: Contact IT Support. For payroll/compensation: Contact Payroll/HR department. Check User Manual for self-help first.',
      },
      {
        id: 'faq-26',
        category: 'general',
        question: 'What if I find a bug or system error?',
        answer: 'Report immediately to IT Support with: (1) Description of the issue, (2) Steps to reproduce, (3) Screenshot if possible, (4) Browser and device information, (5) Date and time of occurrence. Don\'t assume someone else will report it. Early reporting helps quick fixes.',
      },
      {
        id: 'faq-27',
        category: 'general',
        question: 'Can I access ExoTrack from home?',
        answer: 'Usually yes, if you have a company account and internet connection. Some companies restrict access to office network only for security. If you can\'t access from home, check: (1) Are you on VPN? (2) Is remote access allowed? (3) Is your account active? Contact IT if issues persist.',
      },
    ];
  }

  /**
   * Get quick tips data
   */
  getQuickTipsData() {
    return [
      {
        id: 'tip-1',
        icon: 'Clock',
        title: 'Set Clock-Out Reminders',
        description: 'Set a daily phone reminder 10 minutes before end of work to remember clocking out.',
      },
      {
        id: 'tip-2',
        icon: 'Calendar',
        title: 'Plan Leave Early',
        description: 'Book annual leave 2-4 weeks in advance, especially during busy seasons, for better approval chances.',
      },
      {
        id: 'tip-3',
        icon: 'Bell',
        title: 'Enable Notifications',
        description: 'Turn on email and in-app notifications to get instant updates on approvals and important changes.',
      },
      {
        id: 'tip-4',
        icon: 'Download',
        title: 'Export Monthly Records',
        description: 'Download your attendance and leave reports monthly for personal records and backup.',
      },
      {
        id: 'tip-5',
        icon: 'Zap',
        title: 'Request Overtime Before Working',
        description: 'Always submit overtime requests before working extra hours (except emergencies) to ensure payment.',
      },
      {
        id: 'tip-6',
        icon: 'User',
        title: 'Keep Profile Updated',
        description: 'Review and update your emergency contacts, phone number, and address quarterly.',
      },
      {
        id: 'tip-7',
        icon: 'FileText',
        title: 'Track Your Leave Balance',
        description: 'Monitor leave balance monthly to plan vacations and avoid losing unused days.',
      },
      {
        id: 'tip-8',
        icon: 'Lightbulb',
        title: 'Use Dashboard Summary',
        description: 'Check your dashboard daily for quick overview of attendance, hours worked, and notifications.',
      },
      {
        id: 'tip-9',
        icon: 'Lightbulb',
        title: 'Save Approval Emails',
        description: 'Keep approval emails for leave and overtime as proof in case of disputes or audits.',
      },
    ];
  }

  /**
   * Get troubleshooting guides
   */
  getTroubleshootingData() {
    return [
      {
        id: 'ts-1',
        problem: 'Cannot Clock In - Location Error',
        solution: `If you're getting location errors when trying to clock in:

1. Ensure GPS/Location is enabled on your device
2. Grant location permissions to your browser
3. Try moving near a window for better GPS signal
4. If indoors, go outside briefly to get clear signal
5. Refresh the page and try again
6. If at approved location but still getting error, contact your supervisor for manual approval

Prevention: Always enable location services before clocking in.`,
      },
      {
        id: 'ts-2',
        problem: 'Photo Not Uploading',
        solution: `If attendance/leave photos won't upload:

1. Check file size - must be under 10MB
2. Ensure file is in JPG or PNG format
3. Check internet connection is stable
4. Try taking a new photo instead of uploading existing
5. Clear browser cache and cookies
6. Try a different browser (Chrome recommended)
7. Ensure camera permissions are granted
8. Contact IT support if problem persists

Tip: Use the "Take Photo" button rather than uploading for better compatibility.`,
      },
      {
        id: 'ts-3',
        problem: 'Leave Request Won\'t Submit',
        solution: `If your leave request won't submit:

1. Verify all required fields (*) are filled
2. Check date range is valid (start before end date)
3. Ensure you have sufficient leave balance
4. Check file attachments are under 5MB each
5. Try removing attachments and submitting again
6. Clear browser cache and try different browser
7. Check if dates overlap with existing pending request
8. Verify dates aren't in the past (except emergency leave)

If issue continues: Take screenshots and contact HR with error details.`,
      },
      {
        id: 'ts-4',
        problem: 'Forgot Password / Cannot Login',
        solution: `If you can't log in:

1. Click "Forgot Password" on login page
2. Enter your registered email address
3. Check email for password reset link (check spam folder)
4. Link valid for 24 hours - use it quickly
5. Create new password meeting requirements
6. Try logging in with new password

If email doesn't arrive:
- Verify you're using correct email
- Check spam/junk folder
- Wait 5-10 minutes for delivery
- Contact IT support for manual reset

Security: Never share your password with anyone.`,
      },
      {
        id: 'ts-5',
        problem: 'Dashboard Shows Wrong Data',
        solution: `If dashboard displays incorrect information:

1. Refresh your browser (Ctrl+F5 or Cmd+Shift+R)
2. Clear browser cache and cookies
3. Log out and log back in
4. Check if you're viewing correct date/period
5. Compare with detailed history pages
6. Try different browser

If data still wrong:
- Take screenshots showing the issue
- Note specific discrepancies
- Compare with your records
- Report to IT support with details
- May indicate system sync issue requiring admin attention`,
      },
      {
        id: 'ts-6',
        problem: 'Notifications Not Received',
        solution: `If you're not getting notifications:

1. Check notification settings in Profile → Settings
2. Verify email address is correct
3. Check email spam/junk folder
4. Ensure browser notifications are allowed
5. Whitelist ExoTrack email domain
6. Check if notifications are enabled for your account
7. Try toggling notification preferences off and on

Email notifications:
- Add ExoTrack email to contacts
- Check email filters/rules
- Verify email forwarding settings

Browser notifications:
- Allow notification permission in browser
- Check browser notification settings
- Ensure "Do Not Disturb" is off`,
      },
      {
        id: 'ts-7',
        problem: 'Mobile Site Not Working Properly',
        solution: `For mobile browser issues:

1. Use Chrome Mobile or Safari Mobile (recommended)
2. Update browser to latest version
3. Clear browser data (cache, cookies)
4. Ensure JavaScript is enabled
5. Try desktop mode if feature not working
6. Check internet connection (WiFi or 4G)
7. Restart browser app
8. Restart phone if issues persist

Camera issues on mobile:
- Grant camera permission in phone settings
- Check if camera works in other apps
- Try clearing camera app cache
- Use "Take Photo" not "Upload Photo"

Location issues on mobile:
- Enable GPS in phone settings
- Grant location permission for browser
- Ensure location accuracy is set to "High"`,
      },
    ];
  }

  /**
   * Search articles and FAQs
   */
  async search(query) {
    const searchLower = query.toLowerCase();
    const allData = await this.getAllData();

    // Search articles
    const matchingArticles = [];
    allData.categories.forEach(category => {
      category.articles.forEach(article => {
        const titleMatch = article.title.toLowerCase().includes(searchLower);
        const contentMatch = article.content.toLowerCase().includes(searchLower);
        const tagsMatch = article.tags.some(tag => tag.toLowerCase().includes(searchLower));

        if (titleMatch || contentMatch || tagsMatch) {
          matchingArticles.push({
            ...article,
            categoryId: category.id,
            category: category.title,
          });
        }
      });
    });

    // Search FAQs
    const matchingFAQs = allData.faqs.filter(faq => {
      const questionMatch = faq.question.toLowerCase().includes(searchLower);
      const answerMatch = faq.answer.toLowerCase().includes(searchLower);
      return questionMatch || answerMatch;
    });

    return {
      articles: matchingArticles,
      faqs: matchingFAQs,
    };
  }

  /**
   * Get category by ID
   */
  async getCategory(categoryId) {
    const allData = await this.getAllData();
    return allData.categories.find(cat => cat.id === categoryId);
  }

  /**
   * Get FAQs by category
   */
  async getFAQs(category = 'all') {
    const allData = await this.getAllData();
    if (category === 'all') {
      return allData.faqs;
    }
    return allData.faqs.filter(faq => faq.category === category);
  }

  /**
   * Get quick tips
   */
  async getQuickTips() {
    const allData = await this.getAllData();
    return allData.quickTips;
  }

  /**
   * Get troubleshooting guides
   */
  async getTroubleshooting(category = 'all') {
    const allData = await this.getAllData();
    return allData.troubleshooting;
  }
}

export default new UserManualService();
